import React from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Sparkles, ArrowRight, ShieldCheck, Globe2, Users2, BarChart3,
  AlertTriangle, Boxes, Truck, Wrench, Handshake, MessageSquare,
  IdCard, FileText, ShoppingBag, Megaphone, UserSquare2, Briefcase,
  CheckCircle2, Building2, LineChart, ScanLine,
} from "lucide-react";

const MODULES = [
  { icon: AlertTriangle, name: "Issue Reporting",
    desc: "Capture problems with photos, voice notes & escalation across branches." },
  { icon: Boxes, name: "Assets & QR Tags",
    desc: "Track every fridge, POS, printer, AC and generator with auto QR codes." },
  { icon: Truck, name: "Vehicles & Fuel",
    desc: "Fleet, fuel logs, services, accidents, insurance & route reports." },
  { icon: Wrench, name: "Preventive Maintenance",
    desc: "Schedule, assign and audit repairs before things break." },
  { icon: Handshake, name: "Suppliers & Contracts",
    desc: "Vendor scorecards, contracts, deliveries and complaint loops." },
  { icon: MessageSquare, name: "Customer Feedback",
    desc: "QR-coded feedback at branch entrances + complaints workflow." },
  { icon: IdCard, name: "Business Card Repository",
    desc: "Scan international trade-show cards, OCR auto-extracts contacts." },
  { icon: ShoppingBag, name: "Online Shop & Orders",
    desc: "Each tenant gets a public storefront with products, orders, careers." },
  { icon: UserSquare2, name: "Staff Bio Data + HR",
    desc: "Bio data, transfers, attachments, printable PDF profiles." },
  { icon: FileText, name: "Documents & Policy",
    desc: "Operations policy, licenses, NSSF, URA & version-controlled documents." },
  { icon: Megaphone, name: "Internal Comms",
    desc: "Direct messages, announcements, approvals with audit logs." },
  { icon: BarChart3, name: "Executive Dashboard",
    desc: "Real-time KPIs across every branch, every module, instantly." },
];

const STATS = [
  { value: "13", label: "core modules" },
  { value: "5,000+", label: "employees-ready" },
  { value: "50", label: "branches per tenant" },
  { value: "100%", label: "tenant-isolated" },
];

export default function Marketing() {
  return (
    <div className="min-h-screen bg-[#F4F5F1] text-gray-900">
      {/* Top nav */}
      <header className="bg-[#022C22] text-white sticky top-0 z-30 border-b border-emerald-900/60">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 flex items-center gap-4">
          <Link to="/marketing" className="flex items-center gap-2.5">
            <div className="h-9 w-9 rounded-md bg-amber-500 flex items-center justify-center font-display font-bold text-[#022C22]">A</div>
            <div>
              <div className="font-display font-bold text-base leading-tight">AEOIBCMS</div>
              <div className="text-[10px] uppercase tracking-widest text-emerald-200/70">Enterprise SaaS for Supermarkets</div>
            </div>
          </Link>
          <nav className="hidden md:flex items-center gap-1 ml-6 text-sm">
            <a href="#features" className="px-3 py-2 text-emerald-100 hover:bg-emerald-900 rounded-md">Features</a>
            <a href="#why" className="px-3 py-2 text-emerald-100 hover:bg-emerald-900 rounded-md">Why us</a>
            <a href="#how" className="px-3 py-2 text-emerald-100 hover:bg-emerald-900 rounded-md">How it works</a>
            <a href="#pricing" className="px-3 py-2 text-emerald-100 hover:bg-emerald-900 rounded-md">Pricing</a>
          </nav>
          <div className="ml-auto flex items-center gap-2">
            <Link to="/login" data-testid="marketing-login-link"
              className="hidden sm:inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md border border-emerald-200/30 text-white hover:bg-emerald-900">
              Sign in
            </Link>
            <Link to="/signup" data-testid="marketing-cta-signup"
              className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md bg-amber-500 text-emerald-950 font-semibold hover:bg-amber-400">
              <Sparkles size={13} /> Start free trial
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden bg-[#022C22] text-white">
        <div className="absolute inset-0 opacity-[0.07]"
          style={{ backgroundImage: "radial-gradient(circle at 25% 25%, #F59E0B 0, transparent 40%), radial-gradient(circle at 75% 75%, #10B981 0, transparent 40%)" }} />
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 py-16 sm:py-24 grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <div className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-widest px-2.5 py-1 rounded-full bg-emerald-800/60 border border-emerald-500/30 text-emerald-100">
              <Sparkles size={12} className="text-amber-300" /> Trusted by Abrah Supermarket Ltd · Now multi-tenant
            </div>
            <h1 className="font-display font-bold text-4xl sm:text-5xl lg:text-6xl mt-6 leading-[1.05]">
              One platform to run<br />
              <span className="text-amber-400">every branch.</span>
            </h1>
            <p className="mt-5 text-base sm:text-lg text-emerald-100/80 max-w-xl">
              Issue tracking, asset & vehicle management, suppliers, HR, executive dashboards
              and a public e-commerce site — all built for Uganda&apos;s growing supermarket chains.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/signup">
                <Button size="lg" className="bg-amber-500 hover:bg-amber-400 text-emerald-950 font-semibold">
                  Start free trial <ArrowRight size={16} className="ml-1" />
                </Button>
              </Link>
              <a href="#features">
                <Button size="lg" variant="outline"
                  className="border-emerald-200/30 text-white hover:bg-emerald-900 bg-transparent">
                  See features
                </Button>
              </a>
            </div>
            <div className="mt-8 grid grid-cols-2 sm:grid-cols-4 gap-4">
              {STATS.map((s) => (
                <div key={s.label}>
                  <div className="font-display text-2xl text-amber-300">{s.value}</div>
                  <div className="text-[11px] uppercase tracking-wider text-emerald-200/70">{s.label}</div>
                </div>
              ))}
            </div>
          </div>
          <div className="relative">
            <div className="bg-white/5 backdrop-blur-xl border border-emerald-300/20 rounded-xl p-6 shadow-2xl">
              <div className="flex items-center gap-2 mb-4">
                <div className="h-2.5 w-2.5 rounded-full bg-red-400"></div>
                <div className="h-2.5 w-2.5 rounded-full bg-amber-400"></div>
                <div className="h-2.5 w-2.5 rounded-full bg-emerald-400"></div>
                <span className="ml-2 text-[11px] uppercase tracking-widest text-emerald-200/70">Executive Console</span>
              </div>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { l: "Open issues", v: "12", c: "text-amber-300" },
                  { l: "Assets in use", v: "324", c: "text-emerald-300" },
                  { l: "Today's orders", v: "47", c: "text-amber-300" },
                  { l: "Branches", v: "6", c: "text-emerald-300" },
                ].map((k) => (
                  <div key={k.l} className="rounded-lg bg-emerald-950/40 border border-emerald-300/10 p-3">
                    <div className="text-[10px] uppercase tracking-wider text-emerald-200/60">{k.l}</div>
                    <div className={`font-display text-2xl mt-1 ${k.c}`}>{k.v}</div>
                  </div>
                ))}
              </div>
              <div className="mt-4 rounded-lg bg-emerald-950/40 border border-emerald-300/10 p-3">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-[10px] uppercase tracking-wider text-emerald-200/60">Issues by priority</div>
                  <LineChart size={12} className="text-emerald-300" />
                </div>
                <div className="flex items-end gap-1 h-16">
                  {[60, 40, 75, 30, 55, 80, 50].map((h, i) => (
                    <div key={i} className="flex-1 bg-amber-500/80 rounded-t" style={{ height: `${h}%` }} />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="max-w-6xl mx-auto px-4 sm:px-6 py-16 sm:py-24">
        <div className="max-w-2xl">
          <div className="text-[11px] uppercase tracking-widest text-emerald-700 font-semibold">What&apos;s included</div>
          <h2 className="font-display text-3xl sm:text-4xl font-bold mt-3">
            Every module you need.<br />Zero plumbing required.
          </h2>
          <p className="mt-3 text-gray-600">
            AEOIBCMS bundles 13 enterprise modules in one place — no integrations, no
            spreadsheets, no scattered WhatsApp groups.
          </p>
        </div>
        <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {MODULES.map((m) => (
            <div key={m.name}
              className="group bg-white rounded-xl border border-gray-200 p-5 hover:shadow-lg hover:border-emerald-300 transition-all">
              <div className="h-10 w-10 rounded-lg bg-emerald-50 text-emerald-700 flex items-center justify-center group-hover:bg-amber-500 group-hover:text-emerald-950 transition-colors">
                <m.icon size={20} />
              </div>
              <div className="font-display font-semibold text-base mt-3">{m.name}</div>
              <p className="text-sm text-gray-600 mt-1.5 leading-relaxed">{m.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Why us */}
      <section id="why" className="bg-white border-y border-gray-200">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-16 sm:py-24 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="text-[11px] uppercase tracking-widest text-emerald-700 font-semibold">Built for Uganda. Ready for Africa.</div>
            <h2 className="font-display text-3xl sm:text-4xl font-bold mt-3">
              Why supermarkets choose AEOIBCMS
            </h2>
            <div className="mt-6 space-y-4">
              {[
                { i: ShieldCheck, t: "Tenant-isolated by design",
                  d: "Every record is strictly scoped to your supermarket — no leaks, ever. Audited at every endpoint." },
                { i: Globe2, t: "Public storefront in one click",
                  d: "Your own /site/your-name URL — products, orders, careers, QR feedback. No extra setup." },
                { i: ScanLine, t: "OCR business-card capture",
                  d: "Scan supplier and exhibition cards from your phone — GPT-vision extracts every field." },
                { i: Building2, t: "Multi-branch from day one",
                  d: "From 1 branch to 50+ branches, every module is branch-aware out of the box." },
                { i: Users2, t: "Role-based, audit-logged",
                  d: "7 roles · approval workflows · every action audited for accountability." },
                { i: BarChart3, t: "Executive dashboard included",
                  d: "Sales, issues, assets, suppliers, contacts — live KPIs, exportable to PDF/Excel/CSV." },
              ].map((row) => (
                <div key={row.t} className="flex gap-3">
                  <div className="h-9 w-9 rounded-md bg-emerald-50 text-emerald-700 flex items-center justify-center shrink-0">
                    <row.i size={16} />
                  </div>
                  <div>
                    <div className="font-semibold text-sm">{row.t}</div>
                    <div className="text-sm text-gray-600 mt-0.5">{row.d}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="relative">
            <div className="rounded-2xl overflow-hidden border border-gray-200 bg-gradient-to-br from-emerald-900 to-emerald-700 text-white p-8">
              <div className="text-[11px] uppercase tracking-widest text-amber-300">Customer story</div>
              <blockquote className="mt-4 font-display text-xl leading-snug">
                &ldquo;We replaced four separate apps with AEOIBCMS. Our branch managers
                now report issues with photos in under 30 seconds, and the Executive
                Director gets a live dashboard every morning.&rdquo;
              </blockquote>
              <div className="mt-6 flex items-center gap-3">
                <div className="h-10 w-10 rounded-full bg-amber-500 text-emerald-950 flex items-center justify-center font-bold">A</div>
                <div>
                  <div className="font-semibold text-sm">Abrah Supermarket Ltd</div>
                  <div className="text-xs text-emerald-200">6 branches · Kampala, Uganda</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section id="how" className="max-w-6xl mx-auto px-4 sm:px-6 py-16 sm:py-24">
        <div className="text-center max-w-2xl mx-auto">
          <div className="text-[11px] uppercase tracking-widest text-emerald-700 font-semibold">From sign-up to first issue · 5 minutes</div>
          <h2 className="font-display text-3xl sm:text-4xl font-bold mt-3">How it works</h2>
        </div>
        <div className="mt-12 grid sm:grid-cols-3 gap-6">
          {[
            { n: "01", t: "Sign up", d: "Pick your URL slug (e.g. `mymart`), set an admin email and password. Done." },
            { n: "02", t: "Get your console", d: "Instant Main Branch + 5 departments + your public storefront at /site/mymart." },
            { n: "03", t: "Run your supermarket", d: "Add staff, products, suppliers — start reporting issues, tracking assets, viewing KPIs." },
          ].map((s) => (
            <div key={s.n} className="bg-white rounded-xl border border-gray-200 p-6">
              <div className="font-display text-4xl text-amber-500">{s.n}</div>
              <div className="font-display font-semibold text-lg mt-2">{s.t}</div>
              <p className="text-sm text-gray-600 mt-2">{s.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="bg-emerald-50/40 border-y border-emerald-100">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-16 sm:py-24">
          <div className="text-center max-w-2xl mx-auto">
            <div className="text-[11px] uppercase tracking-widest text-emerald-700 font-semibold">Pricing</div>
            <h2 className="font-display text-3xl sm:text-4xl font-bold mt-3">Simple, branch-based pricing</h2>
            <p className="mt-3 text-gray-600">Get started free. Pay only when you grow.</p>
          </div>
          <div className="mt-10 grid md:grid-cols-3 gap-5">
            {[
              { name: "Starter", price: "Free", desc: "1 branch · 5 users", cta: "Start free",
                feats: ["All 13 modules", "Public storefront", "OCR business cards (50/mo)", "Email support"] },
              { name: "Growth", price: "Contact us", featured: true, desc: "Up to 10 branches", cta: "Talk to sales",
                feats: ["Everything in Starter", "Unlimited users", "Approval workflows", "Priority support"] },
              { name: "Enterprise", price: "Custom", desc: "Unlimited branches", cta: "Contact",
                feats: ["Everything in Growth", "Dedicated tenant", "SSO + MFA", "SLA"] },
            ].map((p) => (
              <div key={p.name}
                className={`rounded-xl p-6 ${p.featured ? "bg-[#022C22] text-white border-2 border-amber-400 shadow-2xl scale-[1.02]" : "bg-white border border-gray-200"}`}>
                <div className={`text-[11px] uppercase tracking-widest ${p.featured ? "text-amber-300" : "text-emerald-700"}`}>
                  {p.name}
                </div>
                <div className={`font-display font-bold text-3xl mt-2 ${p.featured ? "text-amber-300" : ""}`}>{p.price}</div>
                <div className={`text-sm mt-1 ${p.featured ? "text-emerald-100/80" : "text-gray-600"}`}>{p.desc}</div>
                <ul className="mt-5 space-y-2">
                  {p.feats.map((f) => (
                    <li key={f} className={`flex items-center gap-2 text-sm ${p.featured ? "text-emerald-50" : "text-gray-700"}`}>
                      <CheckCircle2 size={14} className={p.featured ? "text-amber-400" : "text-emerald-600"} /> {f}
                    </li>
                  ))}
                </ul>
                <Link to="/signup">
                  <Button className={`w-full mt-6 ${p.featured ? "bg-amber-500 hover:bg-amber-400 text-emerald-950" : ""}`}
                    variant={p.featured ? "default" : "outline"}>
                    {p.cta}
                  </Button>
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="bg-[#022C22] text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-16 text-center">
          <h2 className="font-display text-3xl sm:text-4xl font-bold">
            Ready to centralize your supermarket operations?
          </h2>
          <p className="mt-3 text-emerald-100/80">
            Set up your tenant in 5 minutes. No credit card required.
          </p>
          <div className="mt-7 flex justify-center gap-3 flex-wrap">
            <Link to="/signup">
              <Button size="lg" className="bg-amber-500 hover:bg-amber-400 text-emerald-950 font-semibold"
                data-testid="marketing-final-cta">
                Start free trial <ArrowRight size={16} className="ml-1" />
              </Button>
            </Link>
            <Link to="/site/abrah">
              <Button size="lg" variant="outline"
                className="border-emerald-200/30 text-white hover:bg-emerald-900 bg-transparent">
                See a live storefront
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <footer className="bg-emerald-950 text-emerald-100/70 text-xs">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 flex flex-col sm:flex-row gap-3 justify-between items-center">
          <div>© {new Date().getFullYear()} AEOIBCMS · Built for Abrah Supermarket Ltd</div>
          <div className="flex gap-4">
            <Link to="/login" className="hover:text-white">Staff login</Link>
            <Link to="/signup" className="hover:text-white">Start free trial</Link>
            <Link to="/site/abrah" className="hover:text-white">Sample storefront</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
